/**
 * Platforms — `/countries/:cc/admin/platforms`. The GROUP vocabulary's authoring
 * screen (migration 38, owner rulings Q8/Q8-b; API cluster C3).
 *
 * AUTHORED (no Figma exists for the admin surface); assembled from existing
 * primitives in the `TaxGroupsPage` idiom: a create card, the table, an editor card
 * for the selected row, and a confirm dialog for the one action with group-wide
 * blast radius.
 *
 * ===========================================================================
 * WHAT THIS SCREEN SAYS OUT LOUD, BECAUSE THE MODEL IS NEW
 * ===========================================================================
 * ONE LIST FOR THE WHOLE GROUP. `platforms` has no `country_id`
 * (`platforms.controller.ts:14-19`); what a row's edit changes, it changes in every
 * country at once. The per-country half — which values a country OFFERS — is a
 * different screen (`PlatformAvailabilityPage`) on a different route with a
 * different permission, and this screen deliberately has no country column: a
 * `config.global` holder cannot retire a value in ONE country from here, and that
 * absence is pinned server-side (`platforms.e2e-spec.ts` suite (c)).
 *
 * APPLICABILITY IS DATA ON THE ROW (Q8-b). The three checkboxes render the SET's
 * members — schema — and the ticks are the owner's answer; widening a row makes a
 * previously-refused attach succeed with NO REDEPLOY (`platforms.controller.ts:89-94`).
 * Narrowing is not symmetric: the SERVER refuses a narrowing that would strand
 * attached records, naming the count, and this screen shows the attach counts so
 * that refusal is predictable before the round trip.
 *
 * RETIRE IS NOT DELETE. There is no DELETE route at all (the attach tables carry
 * `ON DELETE RESTRICT`); `active: false` withdraws the value from every picker
 * while records that already carry it keep reading correctly.
 *
 * ===========================================================================
 * THE GATE — `config.global`, ON THE READ, MATCHING THE API EXACTLY
 * ===========================================================================
 * Every route behind this screen — the LIST included — runs
 * `assertGlobalConfigAccess`: group admin or `config.global` held in at least one
 * country (`access.ts:132-141`). This is the maintenance view and it shows RETIRED
 * values, which is why the read is not `config.manage` and not open
 * (`platforms.controller.ts:64-69`). The screen therefore refuses without that
 * grant — write controls AND the list, absent, not disabled (D5) — where a
 * `config.manage`-only Country Manager is refused by the API too
 * (`platforms.e2e-spec.ts` suite (b)).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MonitorSmartphone } from 'lucide-react';
import { PageHeader } from '../../components/shell/AppShell';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  EmptyState,
  SkeletonRows,
  TextField,
} from '../../components/ui';
import { useSession } from '../../auth/session';
import { ApiRequestError } from '../../lib/api';
import {
  PLATFORM_TARGETS,
  createPlatform,
  fetchAdminPlatforms,
  patchPlatform,
  vocabularyKeys,
} from './vocabulary-api';
import type { PlatformAdminRow } from './vocabulary-api';
import { ConfirmDialog } from './ConfirmDialog';
import { errorSentence } from './states';
import {
  NoGlobalConfigAccess,
  PLATFORM_TARGET_LABELS,
  appliesToSentence,
} from './vocabulary-states';

/** The API's own sentence when it carries one — the NetworkRules pattern. */
function apiSentence(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) return error.message;
  return errorSentence(error, fallback);
}

/** `"2 CRFs · 1 project"`, or a plain dash when nothing carries the value. */
function inUseSentence(row: PlatformAdminRow): string {
  const parts: string[] = [];
  if (row.attached_crf > 0) parts.push(`${row.attached_crf} CRF${row.attached_crf === 1 ? '' : 's'}`);
  if (row.attached_project > 0) {
    parts.push(`${row.attached_project} project${row.attached_project === 1 ? '' : 's'}`);
  }
  return parts.length === 0 ? '—' : parts.join(' · ');
}

export function PlatformsPage(): React.JSX.Element {
  const { can, isGroupAdmin, accessibleCountries } = useSession();
  const queryClient = useQueryClient();

  /**
   * "Held ANYWHERE", not "held here" — `assertGlobalConfigAccess` reads the token's
   * whole permission map (`hasPermissionAnywhere`), so a CD-only `config.global`
   * override holder standing in IN is served by the API and must be served here.
   * The same union the sidebar's `permsAnywhere` gate uses (`AppShell.tsx:66`).
   */
  const permitted = isGroupAdmin || accessibleCountries.some((code) => can(code, 'config.global'));

  const [name, setName] = useState('');
  const [targets, setTargets] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [retiring, setRetiring] = useState<PlatformAdminRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const platforms = useQuery({
    queryKey: vocabularyKeys.platforms(),
    queryFn: ({ signal }) => fetchAdminPlatforms(signal),
    enabled: permitted,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: vocabularyKeys.platforms() });
    // Every country's availability screen shows `group_active` beside its own answer;
    // a group-wide edit moves that column in all of them.
    void queryClient.invalidateQueries({ queryKey: ['country-platforms'] });
    // The CRF/inquiry pickers cache under per-country keys ('crfs', cc, …); they are
    // not invalidated from here because default staleTime refetches them on mount,
    // and clearing the whole 'crfs' namespace would drop every list cache to move
    // one picker.
  };

  const create = useMutation({
    mutationFn: () =>
      createPlatform({ name: name.trim(), applies_to: targets }),
    onSuccess: () => {
      setName('');
      setTargets([]);
      setActionError(null);
      invalidate();
    },
    onError: (error) => setActionError(apiSentence(error, 'The platform was not added.')),
  });

  const patch = useMutation({
    mutationFn: (input: {
      id: string;
      body: { name?: string; applies_to?: string[]; sort_order?: number; active?: boolean };
    }) => patchPlatform(input.id, input.body),
    onSuccess: () => {
      setRetiring(null);
      setActionError(null);
      invalidate();
    },
    onError: (error) => {
      setRetiring(null);
      setActionError(apiSentence(error, 'The change was not saved.'));
    },
  });

  if (!permitted) return <NoGlobalConfigAccess />;

  const rows = platforms.data ?? [];
  const selected = rows.find((row) => row.id === selectedId) ?? null;

  const toggleTarget = (target: string): void => {
    setTargets((current) =>
      current.includes(target) ? current.filter((entry) => entry !== target) : [...current, target],
    );
  };

  return (
    <div className="flex flex-col gap-4 pb-6">
      <PageHeader
        title="Platforms"
        subtitle="The group's shared platform vocabulary — one list for every country. What a value applies to is a decision on the row; which countries offer it is each country's own screen."
      />

      <div className="flex flex-col gap-4 px-6">
        {actionError === null ? null : <Alert tone="danger">{actionError}</Alert>}

        <Card title="Add a platform">
          <form
            className="flex flex-wrap items-start gap-x-6 gap-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim() === '' || targets.length === 0 || create.isPending) return;
              create.mutate();
            }}
          >
            <div className="w-full max-w-sm">
              <TextField
                label="Name"
                value={name}
                maxLength={60}
                placeholder="e.g. iOS"
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1.5 text-label text-fg-secondary">Applies to</legend>
              {PLATFORM_TARGETS.map((target) => (
                <Checkbox
                  key={target}
                  label={PLATFORM_TARGET_LABELS[target] ?? target}
                  checked={targets.includes(target)}
                  onChange={() => toggleTarget(target)}
                />
              ))}
            </fieldset>
            {/* mt-[26px] aligns with the field boxes — the NetworkRules measurement. */}
            <Button
              type="submit"
              className="mt-[26px]"
              disabled={name.trim() === '' || targets.length === 0 || create.isPending}
            >
              {create.isPending ? 'Adding…' : 'Add platform'}
            </Button>
          </form>
          <p className="text-label text-fg-secondary">
            At least one applicability is required — a value attachable to nothing is a
            decision nobody made, and the server refuses the empty set. The value is offered
            in every country until a country switches it off.
          </p>
        </Card>

        <Card title="Platform vocabulary">
          {platforms.isPending ? (
            <SkeletonRows rows={3} label="Loading platforms" />
          ) : platforms.isError ? (
            /* NOT an empty vocabulary: "could not be read" is not "nothing is authored". */
            <Alert tone="danger" title="The platform vocabulary could not be read">
              {errorSentence(platforms.error, 'The server did not answer.')} Nothing below is
              a count of what is authored — this is a read failure, not an empty register.
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<MonitorSmartphone aria-hidden="true" className="size-5" />}
              title="No platforms authored yet"
              description="Add the first value above. Until one exists, project confirmation refuses with 'nothing to pick' — this screen is where that refusal is cleared."
            />
          ) : (
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-line text-th uppercase text-fg-muted">
                  <th scope="col" className="py-2 pr-3 font-normal">Name</th>
                  <th scope="col" className="py-2 pr-3 font-normal">Applies to</th>
                  <th scope="col" className="py-2 pr-3 font-normal">In use</th>
                  <th scope="col" className="py-2 pr-3 font-normal">Country decisions</th>
                  <th scope="col" className="py-2 pr-3 font-normal">Status</th>
                  <th scope="col" className="py-2 text-right font-normal">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <PlatformRow
                    key={row.id}
                    row={row}
                    selected={row.id === selectedId}
                    onEdit={() => {
                      setActionError(null);
                      setSelectedId(row.id === selectedId ? null : row.id);
                    }}
                    onRetire={() => {
                      setActionError(null);
                      setRetiring(row);
                    }}
                    onReactivate={() => {
                      setActionError(null);
                      patch.mutate({ id: row.id, body: { active: true } });
                    }}
                  />
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {selected === null ? null : (
          <PlatformEditorCard
            key={selected.id}
            row={selected}
            busy={patch.isPending}
            onSave={(body) => patch.mutate({ id: selected.id, body })}
          />
        )}
      </div>

      <ConfirmDialog
        open={retiring !== null}
        title={`Retire '${retiring?.name ?? ''}' group-wide?`}
        body={
          'The value leaves the pickers in every country immediately. Records that ' +
          'already carry it keep it, readable, and it stays listed here as the audit ' +
          'record — retirement can be undone with Reactivate. To withdraw it in ONE ' +
          'country only, use that country’s Platform availability screen instead.'
        }
        confirmLabel="Retire platform"
        busy={patch.isPending}
        onConfirm={() => {
          if (retiring !== null) patch.mutate({ id: retiring.id, body: { active: false } });
        }}
        onCancel={() => setRetiring(null)}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── table row ── */

function PlatformRow({
  row,
  selected,
  onEdit,
  onRetire,
  onReactivate,
}: {
  row: PlatformAdminRow;
  selected: boolean;
  onEdit: () => void;
  onRetire: () => void;
  onReactivate: () => void;
}): React.JSX.Element {
  return (
    <tr className="crf-register-row border-b border-line last:border-b-0">
      <td className={`py-2.5 pr-3 text-body ${row.active ? 'text-fg' : 'text-fg-secondary'}`}>
        {row.name}
      </td>
      <td className="py-2.5 pr-3 text-body13 text-fg-secondary">
        {appliesToSentence(row.applies_to)}
      </td>
      {/* Counts of ATTACHES, never of money (`platforms.service.ts:110-111`) — plain. */}
      <td className="tnum py-2.5 pr-3 text-body13 text-fg-secondary">{inUseSentence(row)}</td>
      <td className="tnum py-2.5 pr-3 text-body13 text-fg-secondary">
        {row.country_decisions === 0 ? '—' : row.country_decisions}
      </td>
      <td className="py-2.5 pr-3">
        {row.active ? null : (
          /* Bordered, unfilled — the agent-keys/network-rules AA measurement. */
          <span className="inline-flex items-center rounded-pill border border-fg-muted px-2 py-0.5 text-label text-fg-secondary">
            Retired
          </span>
        )}
      </td>
      <td className="py-2.5 text-right">
        <span className="inline-flex items-center gap-1">
          <Button variant="quiet" onClick={onEdit}>
            {selected ? 'Close' : 'Edit'}
          </Button>
          {row.active ? (
            <Button variant="quiet" onClick={onRetire}>
              Retire…
            </Button>
          ) : (
            <Button variant="quiet" onClick={onReactivate}>
              Reactivate
            </Button>
          )}
        </span>
      </td>
    </tr>
  );
}

/* ─────────────────────────────────────────────────────────────── editor card ── */

function PlatformEditorCard({
  row,
  busy,
  onSave,
}: {
  row: PlatformAdminRow;
  busy: boolean;
  onSave: (body: { name?: string; applies_to?: string[]; sort_order?: number }) => void;
}): React.JSX.Element {
  const [name, setName] = useState(row.name);
  const [targets, setTargets] = useState<string[]>([...row.applies_to]);
  const [sortOrder, setSortOrder] = useState(String(row.sort_order));

  const toggleTarget = (target: string): void => {
    setTargets((current) =>
      current.includes(target) ? current.filter((entry) => entry !== target) : [...current, target],
    );
  };

  const parsedSort = /^\d+$/.test(sortOrder.trim()) ? Number(sortOrder.trim()) : null;

  /** Only what CHANGED goes on the wire — `UpdatePlatformDto`: absent means untouched. */
  const body: { name?: string; applies_to?: string[]; sort_order?: number } = {};
  if (name.trim() !== row.name) body.name = name.trim();
  const sameTargets =
    targets.length === row.applies_to.length &&
    row.applies_to.every((target) => targets.includes(target));
  if (!sameTargets) body.applies_to = targets;
  if (parsedSort !== null && parsedSort !== row.sort_order) body.sort_order = parsedSort;

  const savable =
    name.trim() !== '' && targets.length > 0 && parsedSort !== null && Object.keys(body).length > 0;

  return (
    <Card title={`Edit '${row.name}'`}>
      <form
        className="flex flex-wrap items-start gap-x-6 gap-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!savable || busy) return;
          onSave(body);
        }}
      >
        <div className="w-full max-w-sm">
          <TextField
            label="Name"
            value={name}
            maxLength={60}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="w-28">
          <TextField
            label="Sort order"
            inputMode="numeric"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          />
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1.5 text-label text-fg-secondary">Applies to</legend>
          {PLATFORM_TARGETS.map((target) => (
            <Checkbox
              key={target}
              label={PLATFORM_TARGET_LABELS[target] ?? target}
              checked={targets.includes(target)}
              onChange={() => toggleTarget(target)}
            />
          ))}
        </fieldset>
        <Button type="submit" className="mt-[26px]" disabled={!savable || busy}>
          {busy ? 'Saving…' : 'Save changes'}
        </Button>
      </form>
      <p className="text-label text-fg-secondary">
        Renaming changes the label on every record that carries this value, in every
        country. Widening applicability makes new attaches possible immediately; a
        narrowing that would strand records already attached is refused by the server,
        naming the count — the In use column above is that count.
      </p>
    </Card>
  );
}
